import { z } from "zod";
import { env } from "@/env";
import { fetchApi, getApiHeaders } from "@/lib/api-helpers";
import type { ThreeDSStatus } from "@fluid-app/fluid-pay-core/types";

// VGS 3DS Initialize Request/Response
export const vgsInitialize3DSRequestSchema = z.object({
  vgs_card_id: z.string(),
});

export const vgsInitialize3DSResponseSchema = z.object({
  tds_method_content: z.string().nullable().optional(),
  merchant_tx_id: z.string().optional(),
  three_ds_xid: z.string().optional(),

  three_ds_required: z.boolean().optional(),
  fingerprinting_required: z.boolean().optional(),
  skip_reason: z.string().optional(),
  message: z.string().optional(),
});

export type VgsInitialize3DSResponse = z.infer<
  typeof vgsInitialize3DSResponseSchema
>;

// VGS 3DS Confirm Request/Response
export const vgsConfirm3DSRequestSchema = z.object({
  transaction_id: z.string(),
});

export const vgsConfirm3DSResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  cart: z.any().optional(),
});

export type VgsConfirm3DSResponse = z.infer<typeof vgsConfirm3DSResponseSchema>;

// Initialize VGS 3DS (device fingerprinting)
export async function initializeVgs3DS({
  cartToken,
  vgsCardId,
}: {
  cartToken: string;
  vgsCardId: string;
}): Promise<VgsInitialize3DSResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/3ds/initialize`,
    method: "POST",
    input: { vgs_card_id: vgsCardId },
    inputSchema: vgsInitialize3DSRequestSchema,
    outputSchema: vgsInitialize3DSResponseSchema,
  });
}

// VGS 3DS Cancel Request/Response
export const vgsCancel3DSRequestSchema = z.object({
  merchant_tx_id: z.string().min(1),
});

export const vgsCancel3DSResponseSchema = z.object({
  canceled: z.boolean().optional(),
  state: z.string().optional(),
});

export type VgsCancel3DSResponse = z.infer<typeof vgsCancel3DSResponseSchema>;

// Cancel a customer-abandoned VGS 3DS challenge attempt.
// Attempt-scoped: the backend no-ops unless merchant_tx_id matches the
// cart's active attempt, so a stale tab can never cancel a newer attempt.
// The 5s abort keeps the Pay button from being stranded behind a hung
// request — on any failure the caller falls back to a local-only close
// and the backend safety net cleans the attempt up at T+6 regardless.
export async function cancelVgs3DS({
  cartToken,
  merchantTxId,
}: {
  cartToken: string;
  merchantTxId: string;
}): Promise<VgsCancel3DSResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/3ds/cancel`,
    method: "POST",
    input: { merchant_tx_id: merchantTxId },
    inputSchema: vgsCancel3DSRequestSchema,
    outputSchema: vgsCancel3DSResponseSchema,
    fetchOptions: { signal: AbortSignal.timeout(5_000) },
  });
}

// Confirm VGS 3DS completion
export async function confirmVgs3DS({
  cartToken,
  transactionId,
}: {
  cartToken: string;
  transactionId: string;
}): Promise<VgsConfirm3DSResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/confirm_3ds_vgs`,
    method: "POST",
    input: { transaction_id: transactionId },
    inputSchema: vgsConfirm3DSRequestSchema,
    outputSchema: vgsConfirm3DSResponseSchema,
  });
}

/**
 * Fetch cart metadata directly (bypassing Zod schema) to check 3DS status.
 * The cart API exposes `metadata` via CartBlueprinter but the FE Zod schema
 * strips it. A raw fetch lets us read the 3DS fields without modifying the
 * shared cart schema.
 */
export async function checkCartVgs3DSStatus(
  cartToken: string,
): Promise<ThreeDSStatus | null> {
  try {
    const res = await fetch(
      `${env.NEXT_PUBLIC_FLUID_API_URL}/carts/${cartToken}`,
      {
        method: "GET",
        headers: getApiHeaders(),
      },
    );

    if (!res.ok) return null;

    const data = await res.json();
    const metadata = data?.cart?.metadata;
    if (!metadata) return null;

    return {
      completed: metadata.three_ds_completed === true,
      failed: !!metadata.three_ds_failed_at,
      canceled: metadata.three_ds_status === "CANCELED",
      merchantTxId: metadata.three_ds_merchant_tx_id as string | undefined,
      status: metadata.three_ds_status as string | undefined,
      errorMessage: metadata.three_ds_message as string | undefined,
    };
  } catch {
    return null;
  }
}

// --- Session-scoped 3DS (cartless card-add) ---
// Siblings of the cart-scoped trio above. When adding a payment method
// without a cart, `POST /fluid_pay/create_payment_method` mints a
// `session_token` if 3DS is required; these clients poll/cancel that
// session. Auth follows the fluid_pay convention: customer JWT in the
// query string.

// The lifecycle states of a session-scoped 3DS attempt.
export const vgs3DSSessionStateSchema = z.enum([
  "initialized",
  "pending",
  "approved",
  "denied",
  "timed_out",
  "cancelled",
  "skipped",
]);

export type Vgs3DSSessionState = z.infer<typeof vgs3DSSessionStateSchema>;

export const vgs3DSSessionStatusResponseSchema = z.object({
  session_token: z.string(),
  state: vgs3DSSessionStateSchema,
  payment_method_id: z.number().nullish(),
  expired: z.boolean(),
  meta: z.unknown(),
});

export type Vgs3DSSessionStatusResponse = z.infer<
  typeof vgs3DSSessionStatusResponseSchema
>;

// Fetch the status of a session-scoped (cartless card-add) 3DS attempt.
// DB-only read on the backend — no upstream 3DS call — so it is safe as the
// polling fallback when the Pusher session channel is unavailable: poll
// until `state` leaves "initialized"/"pending" or `expired` is true, then
// call confirm once.
// Throws ApiError 422 when the session is not found for this customer.
export async function fetchVgs3DSSessionStatus({
  jwt,
  sessionToken,
}: {
  jwt: string;
  sessionToken: string;
}): Promise<Vgs3DSSessionStatusResponse> {
  const query = new URLSearchParams({
    jwt,
    session_token: sessionToken,
  });

  return fetchApi({
    endpoint: `/fluid_pay/create_payment_method/3ds_status?${query}`,
    method: "GET",
    outputSchema: vgs3DSSessionStatusResponseSchema,
  });
}

export const vgs3DSSessionCancelRequestSchema = z.object({
  session_token: z.string().min(1),
});

export const vgs3DSSessionCancelResponseSchema = z.object({
  session_token: z.string(),
  state: vgs3DSSessionStateSchema,
  // Whether THIS call performed the cancel. false is a benign no-op:
  // the session had already resolved before the cancel landed.
  cancelled: z.boolean(),
  meta: z.unknown(),
});

export type Vgs3DSSessionCancelResponse = z.infer<
  typeof vgs3DSSessionCancelResponseSchema
>;

// Cancel a customer-abandoned session-scoped (cartless card-add) 3DS attempt.
// Benign no-op when the session has already resolved (`cancelled: false`) —
// a payment method that was already created is never disturbed.
// The 5s abort keeps the card-add UI from being stranded behind a hung
// request — cancel fires on modal close, so on any failure the caller
// falls back to a local-only close and lets the session expire server-side.
export async function cancelVgs3DSSession({
  jwt,
  sessionToken,
}: {
  jwt: string;
  sessionToken: string;
}): Promise<Vgs3DSSessionCancelResponse> {
  const query = new URLSearchParams({ jwt });

  return fetchApi({
    endpoint: `/fluid_pay/create_payment_method/3ds_cancel?${query}`,
    method: "POST",
    input: { session_token: sessionToken },
    inputSchema: vgs3DSSessionCancelRequestSchema,
    outputSchema: vgs3DSSessionCancelResponseSchema,
    fetchOptions: { signal: AbortSignal.timeout(5_000) },
  });
}
